import React from "react";
import { Container, Typography, Box, Divider, List, ListItem, ListItemIcon, ListItemText, Alert } from "@mui/material";
import SectionTitle from "../../components/layouts/others/title-section.layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faChartLine, faUserTie, faMoneyBillWave, faGavel, faComments, faBookOpen } from "@fortawesome/free-solid-svg-icons";

const POST20231103: React.FC = () => (
    <Container sx={{ mb: 5, maxWidth: "md" }}>
        <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />

        <Box mt={3} mb={2}>
            <Typography
                variant="h6"
                fontWeight={700}
                align="center"
                gutterBottom
                color="primary"
            >
                ALERTE SUR NOS FINANCES !
            </Typography>
            <Divider sx={{ my: 2 }} />
        </Box>

        <Alert
            severity="error"
            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
            sx={{ mb: 4, fontWeight: 600, fontSize: "1.06rem" }}
        >
            Dans quel état le Maire laissera-t-il Bois-le-Roi à la fin de son mandat ? <b>Probablement en faillite !</b>
        </Alert>

        <Typography sx={{ mb: 2 }}>
            Le Maire a entraîné la commune dans une situation financière inextricable. Incapable de se remettre en cause, il assume et persiste dans cette impasse (<b>voir Conseil Municipal du 16/03/2023</b>).{" "}
            <span style={{ color: "#c62828", fontWeight: 700 }}>Tous les voyants sont passés au rouge.</span> Et le mandat n'est pas fini...
        </Typography>

        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} color="secondary">
                <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 6 }} />
                Que s'est-il passé pour que Bois-le-Roi soit en quasi-faillite aujourd'hui ?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
                Pour y voir clair, distinguons <b>3 périodes</b> :
            </Typography>

            <List>
                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemIcon>
                        <FontAwesomeIcon icon={faBookOpen} color="#1976d2" />
                    </ListItemIcon>
                    <ListItemText
                        primary="2018 — Situation saine"
                        secondary={
                            <>
                                <b>Epargne brute largement excédentaire</b> (~1M€) et trésorerie confortable (5,6M€), fruits d’une gestion prudente et d’un retard d’investissements.
                                <br />
                                ⟶ <i>Perspectives favorables en début de mandat Dintilhac</i>
                            </>
                        }
                        primaryTypographyProps={{ fontWeight: 600 }}
                    />
                </ListItem>
                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemIcon>
                        <FontAwesomeIcon icon={faChartLine} color="#ff9800" />
                    </ListItemIcon>
                    <ListItemText
                        primary="2019-2021 — Dégradation douce"
                        secondary={
                            <>
                                Epargne brute stable, <span style={{ color: "#e57373", fontWeight: 500 }}>-60% chaque année</span> par rapport à 2018 suite à la hausse des dépenses.
                            </>
                        }
                        primaryTypographyProps={{ fontWeight: 600 }}
                    />
                </ListItem>
                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemIcon>
                        <FontAwesomeIcon icon={faExclamationTriangle} color="#d32f2f" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Depuis 2022 — Chute libre"
                        secondary={
                            <>
                                <b>Recettes inférieures aux dépenses</b>, explosion des dépenses, épargne brute <span style={{ color: "#b71c1c", fontWeight: 600 }}>quasi-nulle en 2022, négative en 2023 (-200K€)</span>.
                            </>
                        }
                        primaryTypographyProps={{ fontWeight: 600 }}
                    />
                </ListItem>
            </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} color="secondary" gutterBottom>
            <FontAwesomeIcon icon={faUserTie} style={{ marginRight: 6 }} />
            Les décisions inquiétantes du Maire :
        </Typography>

        <List dense>
            <ListItem>
                <ListItemIcon>
                    <FontAwesomeIcon icon={faMoneyBillWave} color="#388e3c" />
                </ListItemIcon>
                <ListItemText
                    primary="Masse salariale en forte hausse"
                    secondary="2018-2023 : +1 million €/an (de 2,7 à 3,7 millions €, +37%)"
                />
            </ListItem>
            <ListItem>
                <ListItemIcon>
                    <FontAwesomeIcon icon={faGavel} color="#795548" />
                </ListItemIcon>
                <ListItemText
                    primary="Cabinets de conseil consultés à outrance"
                    secondary="Innnombrables études, pour la plupart jugées inutiles (ex : BLR2030)."
                />
            </ListItem>
            <ListItem>
                <ListItemIcon>
                    <FontAwesomeIcon icon={faComments} color="#0288d1" />
                </ListItemIcon>
                <ListItemText
                    primary="Fonctionnement de la crèche & médiathèque"
                    secondary="Dépenses supplémentaires attendues."
                />
            </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Alert
            severity="warning"
            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
            sx={{ mb: 3 }}
        >
            <b>L’argent est jeté par les fenêtres — à quand la saisine de la chambre régionale des comptes ?</b>
            <br />
            Les élus du Conseil Municipal devraient tirer la sonnette d’alarme !
        </Alert>

        <Typography>
            Les séances du Conseil Municipal manquent de transparence, le Maire entrave trop souvent les débats :
        </Typography>
        <List dense>
            <ListItem>
                <ListItemText
                    primary="Opposition empêchée de poser ses questions"
                    secondary="Demandent souvent l’accès aux informations, trop tardives ou incomplètes."
                />
            </ListItem>
            <ListItem>
                <ListItemText
                    primary="Documents budgétaires entachés d’erreurs"
                    secondary="Absence d’un véritable Plan Pluriannuel d’Investissement (PPI)."
                />
            </ListItem>
            <ListItem>
                <ListItemText
                    primary="Maintien d’un PPI bâclé, objectif : faire illusion jusqu’en 2026"
                    secondary="L’ardoise sera pour les habitants (les Bacots)."
                />
            </ListItem>
        </List>

        <Alert
            severity="info"
            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
            sx={{ my: 3, fontWeight: 500, fontStyle: "italic" }}
        >
            Arrivera le moment où il faudra rendre compte : il n'y aura plus rien dans la trésorerie et nombre d’investissements seront reportés à une date indéterminée, faute de financement possible.
        </Alert>

        <Typography variant="body1" sx={{ mb: 2, fontWeight: 600, color: "primary.dark" }}>
            Nous demandons au Maire de présenter <span style={{ textDecoration: "underline" }}>EN URGENCE</span> un plan de redressement de nos finances !
        </Typography>
    </Container>
);

export default POST20231103;